import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useI18n } from '../../contexts/I18nContext';
import { confirmPaymentByOrderId } from '../../utils/subscriptionApiUtils';
import './SubscriptionPages.scss';

/** Интервал между запросами confirm-payment (402/429 — тоже ждём столько же). */
const POLL_INTERVAL_MS = 5000;
/** Макс. попыток (~5 мин при 5 с интервале). */
const MAX_ATTEMPTS = 60;
const confirmOkStorageKey = (id: string) => `lazydoc_payment_confirmed_${id}`;

/** Редирект с банка → confirm-payment (без Bearer), при 402 — поллинг; затем refresh (GET active-subscription). */
const PaymentSuccessPage: React.FC = () => {
  const { orderId: orderIdFromPath } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const orderId =
    orderIdFromPath?.trim() ||
    searchParams.get('order_id')?.trim() ||
    searchParams.get('orderId')?.trim() ||
    searchParams.get('OrderId')?.trim() ||
    undefined;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useI18n();
  const [status, setStatus] = useState<'pending' | 'error'>('pending');
  const [message, setMessage] = useState('');

  const navigateRef = useRef(navigate);
  const enqueueSnackbarRef = useRef(enqueueSnackbar);
  const tRef = useRef(t);
  navigateRef.current = navigate;
  enqueueSnackbarRef.current = enqueueSnackbar;
  tRef.current = t;

  useEffect(() => {
    console.log('[PaymentSuccess] effect start', {
      pathOrderId: orderIdFromPath,
      queryOrderId: searchParams.get('order_id') || searchParams.get('orderId') || searchParams.get('OrderId'),
      resolvedOrderId: orderId,
      href: window.location.href,
    });

    if (!orderId) {
      console.log('[PaymentSuccess] no orderId -> error state');
      setStatus('error');
      setMessage(tRef.current('subscription.paymentNoOrder'));
      return;
    }

    const ac = new AbortController();
    const signal = ac.signal;
    console.log('[PaymentSuccess] polling initialized', {
      orderId,
      pollIntervalMs: POLL_INTERVAL_MS,
      maxAttempts: MAX_ATTEMPTS,
    });

    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        if (signal.aborted) {
          resolve();
          return;
        }
        const id = window.setTimeout(() => resolve(), ms);
        signal.addEventListener(
          'abort',
          () => {
            clearTimeout(id);
            resolve();
          },
          { once: true }
        );
      });

    const finishAfterConfirm = async (): Promise<void> => {
      if (signal.aborted) return;
      console.log('[PaymentSuccess] finishAfterConfirm: begin redirect flow', { orderId });
      const snackbarKey = sessionStorage.getItem('subscriptionPostPaymentSnackbar');
      console.log('[PaymentSuccess] sessionStorage before cleanup', {
        redirect: sessionStorage.getItem('subscriptionPostPaymentRedirect'),
        snackbarKey,
        confirmedKey: sessionStorage.getItem(confirmOkStorageKey(orderId)),
      });
      sessionStorage.removeItem('subscriptionPostPaymentRedirect');
      if (snackbarKey) {
        sessionStorage.removeItem('subscriptionPostPaymentSnackbar');
      }
      const messageKey = snackbarKey || 'subscription.paymentSuccess';
      console.log('[PaymentSuccess] enqueue success snackbar', { messageKey });
      enqueueSnackbarRef.current(tRef.current(messageKey), { variant: 'success' });
      sessionStorage.removeItem(confirmOkStorageKey(orderId));
      console.log('[PaymentSuccess] navigate -> /dialogs');
      navigateRef.current('/dialogs', { replace: true });
    };

    const run = async () => {
      if (sessionStorage.getItem(confirmOkStorageKey(orderId))) {
        console.log('[PaymentSuccess] confirmed flag already exists -> finishing immediately');
        try {
          await finishAfterConfirm();
        } catch (e) {
          if (signal.aborted) return;
          sessionStorage.removeItem(confirmOkStorageKey(orderId));
          console.error('[PaymentSuccess] finishAfterConfirm failed', e);
          setStatus('error');
          setMessage(e instanceof Error ? e.message : tRef.current('subscription.error'));
        }
        return;
      }

      for (let i = 0; i < MAX_ATTEMPTS && !signal.aborted; i++) {
        try {
          console.log('[PaymentSuccess] confirm attempt', { attempt: i + 1, orderId });
          const result = await confirmPaymentByOrderId(orderId, { signal });
          if (signal.aborted) return;

          if (result === 'pending') {
            console.log('[PaymentSuccess] confirm pending (402/429), waiting', { waitMs: POLL_INTERVAL_MS });
            await sleep(POLL_INTERVAL_MS);
            if (signal.aborted) return;
            continue;
          }

          console.log('[PaymentSuccess] confirm success', result);
          sessionStorage.setItem(confirmOkStorageKey(orderId), '1');
          console.log('[PaymentSuccess] confirmed flag saved', { key: confirmOkStorageKey(orderId) });
          try {
            await finishAfterConfirm();
          } catch (e) {
            if (signal.aborted) return;
            sessionStorage.removeItem(confirmOkStorageKey(orderId));
            console.error('[PaymentSuccess] finishAfterConfirm failed after confirm success', e);
            throw e;
          }
          return;
        } catch (e) {
          if (signal.aborted) return;
          if (e instanceof Error && e.name === 'AbortError') return;
          console.error('[PaymentSuccess] confirm failed -> error state', e);
          setStatus('error');
          setMessage(e instanceof Error ? e.message : tRef.current('subscription.error'));
          return;
        }
      }
      if (!signal.aborted) {
        console.error('[PaymentSuccess] polling timeout -> error state', {
          attempts: MAX_ATTEMPTS,
          intervalMs: POLL_INTERVAL_MS,
        });
        setStatus('error');
        setMessage(tRef.current('subscription.paymentConfirmTimeout'));
      }
    };

    void run();
    return () => {
      console.log('[PaymentSuccess] effect cleanup -> abort polling');
      ac.abort();
    };
  }, [orderId, orderIdFromPath, searchParams]);

  return (
    <div className="subscription-page subscription-page--modal">
      <div className="subscription-modal">
        {status === 'pending' && (
          <>
            <div className="subscription-modal__loader" />
            <p>{t('subscription.confirmingPayment')}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <h2>{t('subscription.paymentErrorTitle')}</h2>
            <p>{message}</p>
            <button
              type="button"
              className="subscription-btn subscription-btn--primary subscription-btn--block"
              onClick={() => navigate('/subscription/tariffs')}
            >
              {t('subscription.backToTariffs')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
